import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useGoogleServices } from "@ryde/redux";
import {
  API_BASE_LANDINGS,
  appLanguageSelector,
  castErrorResult,
  createErrorResult,
  createSuccessResult,
  errors,
  ICoordinate,
  ILocationInfo,
  IPlacePrediction,
  IValidationsYupRoot,
  SimpleResult,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { LandingAddressAutocomplete } from "./landing-address-autocomplete";

interface ILandingAddressWrapperProps {
  value: string | ILocationInfo;
  label?: string | null;
  placeholder?: string | null;
  onChange?: (value: Partial<ILocationInfo>) => void;
  error?: boolean;
  formValidation?: IValidationsYupRoot | null;
  inputId?: string;
}

export const LandingAddressWrapper: FC<ILandingAddressWrapperProps> = ({
  value,
  label,
  placeholder,
  onChange,
  error,
  formValidation,
  inputId,
}) => {
  const {
    getCoordinateByPlaceId,
    getPlaceDetailsByPlaceId,
    searchPlacesByInput,
    searchAddressByGeo,
  } = useGoogleServices(API_BASE_LANDINGS, true);
  const [currentValue, setCurrentValue] = useState<IPlacePrediction | null>(
    null,
  );
  const [currentOptions, setCurrentOptions] = useState<IPlacePrediction[]>([]);
  const { t } = useTranslation();
  const language = useRecoilValue(appLanguageSelector);

  const fetchDefaultData = useEvent(
    async (
      defaultValue: string,
      defaultCoordinate: ICoordinate | undefined,
    ): Promise<
      SimpleResult<{
        nextCurrentPlace: IPlacePrediction;
        nextCurrentCoordinate: ICoordinate | undefined;
      }>
    > => {
      let placesResults = null;

      try {
        if (defaultCoordinate) {
          const searchByGeoResponse = await searchAddressByGeo({
            geoParams: {
              location: defaultCoordinate,
              language,
            },
          });

          placesResults = {
            data: searchByGeoResponse?.data ?? null,
            success: !!searchByGeoResponse?.success,
            ...(searchByGeoResponse?.error
              ? { error: searchByGeoResponse.error }
              : {}),
          };
        } else {
          const searchByInputResponse = await searchPlacesByInput({
            inputParams: {
              input: defaultValue,
              language,
            },
          });

          placesResults = {
            data: searchByInputResponse?.data?.[0] ?? null,
            success: !!searchByInputResponse?.success,
            ...(searchByInputResponse?.error
              ? { error: searchByInputResponse.error }
              : {}),
          };
        }

        if (!placesResults.success) {
          return castErrorResult(placesResults);
        }

        if (!placesResults.data) {
          return createErrorResult(
            errors.map.searchPlacesByInput,
            "No results",
          );
        }

        const nextCurrentPlace = placesResults.data;
        let nextCurrentCoordinate: ICoordinate | undefined = defaultCoordinate;

        if (!defaultCoordinate) {
          const coordinateResult = await getCoordinateByPlaceId(
            placesResults.data?.placeId,
          );

          if (!coordinateResult.success) {
            return coordinateResult as SimpleResult<any>;
          }

          nextCurrentCoordinate = coordinateResult.data;
        }

        return createSuccessResult({
          nextCurrentPlace,
          nextCurrentCoordinate,
        });
      } catch (err) {
        return createErrorResult(
          errors.common.technicalProblemOccurred,
          "Error while fetching location",
        );
      }
    },
  );

  const handleFetchDefaultData = useEvent(
    async (
      defaultValue: string,
      defaultCoordinate: ICoordinate | undefined,
    ) => {
      const dataResult = await fetchDefaultData(
        defaultValue,
        defaultCoordinate,
      );

      if (!dataResult.success) {
        return warnAndToastSimpleResult(dataResult, t);
      }
    },
  );

  useEffect(() => {
    const defaultValue = typeof value === "string" ? value : value?.fullAddress;
    const defaultCoordinate =
      typeof value !== "string" && value?.latitude && value?.longitude
        ? { lat: value.latitude, lng: value.longitude }
        : undefined;

    setCurrentValue({ text: { text: defaultValue } } as IPlacePrediction);

    if (defaultValue && defaultValue !== "") {
      handleFetchDefaultData(defaultValue, defaultCoordinate);
    }
  }, [handleFetchDefaultData, value]);

  const handleParentUpdate = (
    fullAddress: string | undefined,
    latitude: number | undefined,
    longitude: number | undefined,
  ): void => {
    onChange?.({
      fullAddress,
      latitude,
      longitude,
    });
  };

  const handleCurrentValueChange = useEvent(
    async (
      newValue: IPlacePrediction | null,
      sessionToken?: string | null,
    ): Promise<void> => {
      setCurrentValue(newValue);

      if (newValue) {
        const locationResult = await getPlaceDetailsByPlaceId({
          placeId: newValue?.placeId,
          sessionToken,
          languageCode: language,
        });

        if (!locationResult.success) {
          return warnAndToastSimpleResult(locationResult, t);
        }

        handleParentUpdate(
          newValue?.text.text,
          locationResult.data?.lat,
          locationResult.data?.lng,
        );
      } else {
        handleParentUpdate("", undefined, undefined);
      }
    },
  );

  const handleOptionsChange = useCallback((options: IPlacePrediction[]) => {
    setCurrentOptions(options);
  }, []);

  return (
    <LandingAddressAutocomplete
      value={currentValue}
      defaultOptions={currentOptions}
      onChange={handleCurrentValueChange}
      onOptionsChange={handleOptionsChange}
      label={label}
      placeholder={placeholder}
      sx={{ marginBottom: "5px", width: "100%" }}
      error={error}
      formValidation={formValidation}
      inputId={inputId}
    />
  );
};
